/**
 * IndexedDB wrapper for Force2 FieldOps offline mode.
 *
 * Three logical stores:
 *   - projects  : `{ id, cachedAt, data }` — full project detail (incl. files, progress)
 *   - cache     : `{ url, cachedAt, status, data }` — generic GET response cache
 *   - queue     : `{ id (auto), createdAt, kind, ...payload }` — pending mutations
 *
 * The queue records carry either a plain JSON payload (Tailgate, JSA, PATCH)
 * or a Blob (photo/screenshot/test-doc upload) plus metadata. Blobs are
 * stored as-is — IndexedDB natively supports Blob/File without encoding.
 */
import { openDB } from "idb";

const DB_NAME = "force2_fieldops_offline";
const DB_VERSION = 1;

let _dbPromise = null;

export function getDb() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("projects")) {
          db.createObjectStore("projects", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("cache")) {
          db.createObjectStore("cache", { keyPath: "url" });
        }
        if (!db.objectStoreNames.contains("queue")) {
          const s = db.createObjectStore("queue", {
            keyPath: "id",
            autoIncrement: true,
          });
          s.createIndex("byProject", "projectId");
        }
        if (!db.objectStoreNames.contains("meta")) {
          db.createObjectStore("meta", { keyPath: "key" });
        }
      },
    });
  }
  return _dbPromise;
}

// ---------- Generic GET cache ----------
export async function putCache(url, status, data) {
  const db = await getDb();
  await db.put("cache", { url, status, data, cachedAt: Date.now() });
}

export async function getCache(url) {
  const db = await getDb();
  return db.get("cache", url);
}

// ---------- Project detail cache ----------
export async function putProjectOffline(id, data) {
  const db = await getDb();
  await db.put("projects", { id, data, cachedAt: Date.now() });
}

export async function getProjectOffline(id) {
  const db = await getDb();
  return db.get("projects", id);
}

export async function removeProjectOffline(id) {
  const db = await getDb();
  await db.delete("projects", id);
}

export async function listProjectsOffline() {
  const db = await getDb();
  return db.getAll("projects");
}

// ---------- Mutation queue ----------
/**
 * Enqueue a pending mutation. `kind` controls how the sync worker replays it.
 *   - "upload"   : multipart upload (photo / screenshot / test_doc).
 *                  payload = { projectId, formFields:{kind,category,requirement_key,notes,...}, file:Blob, filename, contentType }
 *   - "tailgate" : POST /projects/:id/tailgate  body=JSON
 *   - "jsa"      : POST /projects/:id/jsa       body=JSON
 *   - "patch"    : PATCH any url with JSON body
 *   - "post"     : POST any url with JSON body  (generic)
 */
export async function enqueue(item) {
  const db = await getDb();
  const record = {
    ...item,
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  return db.add("queue", record);
}

export async function listQueue() {
  const db = await getDb();
  return db.getAll("queue");
}

export async function queueSize() {
  const db = await getDb();
  return db.count("queue");
}

export async function removeQueued(id) {
  const db = await getDb();
  await db.delete("queue", id);
}

export async function updateQueued(id, patch) {
  const db = await getDb();
  const existing = await db.get("queue", id);
  if (!existing) return;
  await db.put("queue", { ...existing, ...patch });
}

// ---------- Meta (last sync time, "offline by user" flag, etc.) ----------
export async function getMeta(key) {
  const db = await getDb();
  const r = await db.get("meta", key);
  return r ? r.value : undefined;
}

export async function setMeta(key, value) {
  const db = await getDb();
  await db.put("meta", { key, value });
}
